// Páginas legales públicas: política de datos y términos y condiciones.
//
// Van sin sesión a propósito: el salón tiene que poder pasarle el enlace a sus
// clientes, y un titular que quiera ejercer sus derechos no tiene por qué
// tener usuario en el sistema.
//
// Las dos páginas son la misma estructura con distinto contenido, así que hay
// un solo componente y dos envoltorios.

import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, FileText } from 'lucide-react';
import {
  POLITICA_SECCIONES, POLITICA_VERSION, POLITICA_ACTUALIZADA, RESPONSABLE,
} from '@/data/politicaDatos';
import {
  TERMINOS_SECCIONES, TERMINOS_VERSION, TERMINOS_ACTUALIZADA,
} from '@/data/terminos';

/** Negritas con ** sin traer una biblioteca entera para eso. */
const conNegritas = (texto: string): React.ReactNode =>
  texto.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>,
  );

const cuerpo = (texto: string): React.ReactNode => {
  const bloques: React.ReactNode[] = [];
  let lista: string[] = [];

  const cerrarLista = () => {
    if (lista.length === 0) return;
    bloques.push(
      <ul key={`l-${bloques.length}`} className="my-3 list-disc space-y-1.5 pl-6 text-slate-600">
        {lista.map((it, i) => <li key={i}>{conNegritas(it.replace(/^[-*]\s+/, ''))}</li>)}
      </ul>,
    );
    lista = [];
  };

  for (const linea of texto.split('\n')) {
    if (/^[-*]\s+/.test(linea)) { lista.push(linea); continue; }
    cerrarLista();
    if (linea.trim() === '') continue;
    bloques.push(
      <p key={`p-${bloques.length}`} className="my-3 leading-relaxed text-slate-600">
        {conNegritas(linea)}
      </p>,
    );
  }
  cerrarLista();
  return bloques;
};

interface DocumentoLegalProps {
  titulo: string;
  subtitulo: string;
  icono: typeof ShieldCheck;
  version: string;
  actualizada: string;
  secciones: { id: string; titulo: string; cuerpo: string }[];
}

function DocumentoLegal({
  titulo, subtitulo, icono: Icono, version, actualizada, secciones,
}: DocumentoLegalProps) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <Link
            to="/login"
            className="inline-flex min-h-[44px] items-center gap-2 text-sm text-slate-600 hover:text-emerald-700"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Volver
          </Link>
          <span className="text-sm font-semibold text-slate-900">Grooming ERP</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
            <Icono className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {titulo}
            </h1>
            <p className="mt-1 text-sm text-slate-500">{subtitulo}</p>
          </div>
        </div>

        <p className="mt-4 text-xs text-slate-400">
          Versión {version} · Actualizada el {actualizada}
        </p>

        <div className="mt-8 space-y-9">
          {secciones.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-20">
              <h2 className="font-display text-lg font-semibold text-slate-900">{s.titulo}</h2>
              <div className="mt-1 text-[15px]">{cuerpo(s.cuerpo)}</div>
            </section>
          ))}
        </div>

        <footer className="mt-12 border-t border-slate-200 pt-6 text-xs text-slate-400">
          <p>
            {RESPONSABLE.nombre} · NIT {RESPONSABLE.nit} · {RESPONSABLE.direccion}
          </p>
          <p className="mt-1">
            <a href={`mailto:${RESPONSABLE.emailContacto}`} className="underline hover:text-slate-600">
              {RESPONSABLE.emailContacto}
            </a>
          </p>
          <p className="mt-2">
            Diseñado y desarrollado por{' '}
            <a
              href="https://netcloudnetworking.com"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded underline hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
            >
              Netcloud Networking
            </a>{' '}
            · © {new Date().getFullYear()}
          </p>
        </footer>
      </main>
    </div>
  );
}

export function PoliticaDatosPage() {
  return (
    <DocumentoLegal
      titulo="Política de Tratamiento de Datos Personales"
      subtitulo="Ley 1581 de 2012 y Decreto 1377 de 2013 · República de Colombia"
      icono={ShieldCheck}
      version={POLITICA_VERSION}
      actualizada={POLITICA_ACTUALIZADA}
      secciones={POLITICA_SECCIONES}
    />
  );
}

export function TerminosCondicionesPage() {
  return (
    <DocumentoLegal
      titulo="Términos y Condiciones"
      subtitulo="Condiciones del servicio de Grooming ERP"
      icono={FileText}
      version={TERMINOS_VERSION}
      actualizada={TERMINOS_ACTUALIZADA}
      secciones={TERMINOS_SECCIONES}
    />
  );
}
